var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import MARK_ALL_NOTIFICATIONS_AS_READ from 'api/mutations/MARK_ALL_NOTIFICATIONS_AS_READ';
import MARK_NOTIFICATION_AS_READ from 'api/mutations/MARK_NOTIFICATION_AS_READ';
import GET_MENTION_NOTIFICATIONS from 'api/queries/GET_MENTION_NOTIFICATIONS';
import GET_ALL_NOTIFICATIONS from 'api/queries/GET_NOTIFICATIONS';
import GET_UNREAD_NOTIFICATIONS from 'api/queries/GET_UNREAD_NOTIFICATIONS';
const initialState = {
    items: [],
    unreadItems: [],
    mentionItems: [],
    status: {
        fetch: 'idle',
        fetchUnread: 'idle',
        fetchMentions: 'idle',
        markAsRead: 'idle',
    },
    errors: {
        fetch: null,
        fetchUnread: null,
        fetchMentions: null,
        markAsRead: null,
    },
    refetchFlags: {
        fetch: false,
        fetchUnread: false,
        fetchMentions: false,
        markAsRead: false,
    },
};
export const fetchNotifications = createAsyncThunk('notifications/fetchNotifications', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const notificationsData = yield client.query({
            query: GET_ALL_NOTIFICATIONS,
            variables: { page: 0, size: 10 },
            fetchPolicy: 'no-cache',
        });
        return notificationsData.data.getMyNotifications.content || [];
    }
    catch (error) {
        const apolloError = error;
        // eslint-disable-next-line no-console
        console.error('Error fetching notifications:', apolloError.message);
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchUnreadNotifications = createAsyncThunk('notifications/fetchUnreadNotifications', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const notificationsData = yield client.query({
            query: GET_UNREAD_NOTIFICATIONS,
            fetchPolicy: 'no-cache',
        });
        return notificationsData.data.getMyUnreadNotifications || [];
    }
    catch (error) {
        const apolloError = error;
        // eslint-disable-next-line no-console
        console.error('Error fetching unread notifications:', apolloError.message);
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchMentionNotifications = createAsyncThunk('notifications/fetchMentionNotifications', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const notificationsData = yield client.query({
            query: GET_MENTION_NOTIFICATIONS,
            variables: { page: 0, size: 10 },
            fetchPolicy: 'no-cache',
        });
        return notificationsData.data.getMyMentionNotifications.content || [];
    }
    catch (error) {
        const apolloError = error;
        // eslint-disable-next-line no-console
        console.error('Error fetching mention notifications:', apolloError.message);
        return rejectWithValue(apolloError.message);
    }
}));
export const markNotificationAsRead = createAsyncThunk('notifications/markNotificationAsRead', (notificationId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: MARK_NOTIFICATION_AS_READ,
            variables: { notificationId },
        });
        return notificationId;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const markAllNotificationsAsRead = createAsyncThunk('notifications/markAllNotificationsAsRead', (_, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const notificationsData = yield client.mutate({
            mutation: MARK_ALL_NOTIFICATIONS_AS_READ,
        });
        return notificationsData.data.markAllNotificationsAsRead;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        resetNotifications: (state) => {
            state.items = [];
            state.unreadItems = [];
            state.status.fetch = 'idle';
            state.status.fetchUnread = 'idle';
            state.status.markAsRead = 'idle';
            state.errors.fetch = null;
            state.errors.fetchUnread = null;
            state.errors.markAsRead = null;
            state.refetchFlags.fetch = false;
            state.refetchFlags.fetchUnread = false;
            state.refetchFlags.markAsRead = false;
        },
        addRealTimeNotification: (state, action) => {
            const newNotification = action.payload;
            // Check if notification already exists (avoid duplicates)
            const existingIndex = state.items.findIndex((n) => n.id === newNotification.id);
            if (existingIndex === -1) {
                // Add to the beginning of the list (most recent first)
                state.items.unshift(newNotification);
                // Add to unread items if not read
                if (!newNotification.isRead) {
                    state.unreadItems.unshift(newNotification);
                }
            }
            else {
                // Update existing notification
                state.items[existingIndex] = newNotification;
                if (!newNotification.isRead) {
                    const unreadIndex = state.unreadItems.findIndex((n) => n.id === newNotification.id);
                    if (unreadIndex === -1) {
                        state.unreadItems.unshift(newNotification);
                    }
                    else {
                        state.unreadItems[unreadIndex] = newNotification;
                    }
                }
                else {
                    // Remove from unread if marked as read
                    state.unreadItems = state.unreadItems.filter((n) => n.id !== newNotification.id);
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
            state.status.fetch = 'succeeded';
            state.items = Array.isArray(action.payload) ? action.payload : [];
        })
            .addCase(fetchNotifications.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
            state.items = []; // Ensure items is always an array even on error
        })
            .addCase(fetchUnreadNotifications.pending, (state) => {
            state.status.fetchUnread = 'loading';
        })
            .addCase(fetchUnreadNotifications.fulfilled, (state, action) => {
            state.status.fetchUnread = 'succeeded';
            state.unreadItems = Array.isArray(action.payload) ? action.payload : [];
        })
            .addCase(fetchUnreadNotifications.rejected, (state, action) => {
            state.status.fetchUnread = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
            state.unreadItems = []; // Ensure unreadItems is always an array even on error
        })
            .addCase(fetchMentionNotifications.pending, (state) => {
            state.status.fetchMentions = 'loading';
        })
            .addCase(fetchMentionNotifications.fulfilled, (state, action) => {
            state.status.fetchMentions = 'succeeded';
            state.mentionItems = Array.isArray(action.payload) ? action.payload : [];
        })
            .addCase(fetchMentionNotifications.rejected, (state, action) => {
            state.status.fetchMentions = 'failed';
            state.errors.fetchMentions = action.error.message || 'Error fetching mention notifications';
            state.mentionItems = []; // Ensure mentionItems is always an array even on error
        })
            .addCase(markNotificationAsRead.pending, (state) => {
            state.status.markAsRead = 'loading';
        })
            .addCase(markNotificationAsRead.fulfilled, (state, action) => {
            state.status.markAsRead = 'succeeded';
            state.items = state.items.map((n) => n.id === action.payload ? Object.assign(Object.assign({}, n), { isRead: true }) : n);
            state.unreadItems = state.unreadItems.filter((notification) => notification.id !== action.payload);
        })
            .addCase(markNotificationAsRead.rejected, (state, action) => {
            state.status.markAsRead = 'failed';
            state.errors.markAsRead = action.error.message || 'Error fetching components';
        })
            .addCase(markAllNotificationsAsRead.pending, (state) => {
            state.status.markAsRead = 'loading';
        })
            .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
            state.status.markAsRead = 'succeeded';
            state.items = state.items.map((n) => (Object.assign(Object.assign({}, n), { isRead: true })));
            state.unreadItems = [];
        });
    },
});
export const { resetNotifications, addRealTimeNotification } = notificationsSlice.actions;
export const selectNotificationsStatus = (state) => state.notifications.status;
export const selectNotificationsErrors = (state) => state.notifications.errors;
export const selectNotificationsRefetchFlags = (state) => state.notifications.refetchFlags;
export const selectNotifications = (state) => state.notifications.items;
export const selectUnreadNotifications = (state) => state.notifications.unreadItems;
export const selectMentionNotifications = (state) => state.notifications.mentionItems;
export default notificationsSlice.reducer;
